<template>
  <div class="deck-terms" :class="{ review }">
    <div class="heading-dropdown">
      <h2 class="h3">
        Explore the Flashcards:
      </h2>
      <Dropdown
        v-if="showDropdown" calculator :selected-title="selectedCardType"
        :selected="selectedCardType"
        :radio-buttons="statuses" @change="(currentCardType) => currentSelectedCardType(currentCardType)"
      />
    </div>
    <div
      v-for="(card, index) in currentCards" :key="index" class="card"
      :class="card.status || ''"
    >
      <span v-if="card.status" class="status">{{ status(card.status) }}</span>
      <span class="card-number">{{ card.order }} of {{ currentCardCount }}</span>
      <div class="card-holder">
        <div class="question col col-6">
          <CardContent
            type="question" :html="card.question" place="terms"
            deck-terms
          />
        </div>
        <div class="answer col col-6">
          <CardContent
            type="answer" :html="card.answer" place="terms"
            deck-terms
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '../components/Dropdown.vue';
import CardContent from './CardContent.vue';

export default {
  components: {
    Dropdown, CardContent,
  },
  props: {
    deck: { type: Object, required: true, default: () => ({}) },
    review: Boolean,
  },
  data() {
    return {
      selectedCardType: 'all',
      currentCards: this.deck.cards,
      currentCardCount: this.deck.cardCount,
    };
  },
  computed: {
    showDropdown() {
      return this.$store.state.auth.user && (this.currentCards.some((c) => c.status) || (!this.currentCards.some((c) => c.status) && this.selectedCardType !== 'all'));
    },
    statuses() {
      return this.review
        ? {
          all: 'All Cards',
          reviewed: 'Reviewed Cards',
          'not-reviewed': 'Not Reviewed Cards',
        }
        : {
          all: 'All Cards',
          known: 'Known Cards',
          skipped: 'Skipped Cards',
          unknown: 'Still Learning Cards',
        };
    },
  },
  methods: {
    async currentSelectedCardType(currentCardType) {
      this.selectedCardType = currentCardType;
      const deck = await this.$axios.$get(`/flashcards/${this.$route.params.slug}${this.$route.query.review ? '?review=1' : '?initial_data=1'}${this.selectedCardType ? `&status=${this.selectedCardType}` : ''}`).catch(() => false);
      this.currentCards = deck.cards;
    },
    status(status) {
      switch (status) {
        case 'unknown':
          return 'Still Learning';
        case 'not-reviewed':
          return 'Not Reviewed';
        default:
          return status;
      }
    },
  },
};
</script>

<style lang="scss">
.deck-terms {
  padding-bottom: 120px;

  .h3 {
    margin: var(--spacing-40) 0;
  }

  .heading-dropdown {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .dropdown {
      height: 52px;
      width: 320px;
      border-radius: 5px;
      border: 0.89px solid rgba(0, 12, 31, 0.18);
      background: var(--lights-and-shades-main-light, #FFF);
      box-shadow: none;
      font-size: 18px;
      line-height: 120%;

      .list-head {
        padding: 16px 8px 16px 12px;

        .colorize {
          font-size: 18px;
          font-weight: 400;
          line-height: 120%;
        }
      }

      .list {
        top: 102.9%;

        li {
          &.radio div .custom-checkbox + label {
            padding: 14px 0;

            span:not(.cube) {
              padding: 0;
              font-size: 18px;
              font-weight: 400;
            }
          }

          &:hover,
          &.hovered,
          &.selected {
            &.radio div .custom-checkbox + label span:not(.cube) {
              font-weight: 600;
            }
          }
        }
      }
    }
  }

  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--accent);
    border-radius: 10px;
    background: var(--main-light);
    margin-bottom: var(--spacing-22);
    padding: var(--spacing-40) var(--spacing-50);
    height: 400px;

    &:last-of-type {
      margin-bottom: 0;
    }

    .status {
      position: absolute;
      left: 16px;
      top: 22px;
      border-radius: 100px;
      color: var(--main-light-shade-1);
      border: 2px solid transparent;
      font-size: 14px;
      font-weight: 600;
      line-height: 23px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 var(--spacing-12);
      text-transform: capitalize;
    }

    .card-number {
      text-align: center;
      margin-bottom: var(--spacing-20);
      font-size: 18px;
      font-weight: 500;
      line-height: 110%;
      color: var(--main-dark-light-3);
    }

    .card-holder {
      height: calc(100% - 40px);

      .question {
        padding-left: 0;
        padding-right: var(--spacing-30);
        position: relative;
        height: 100%;

        &:after {
          content: '';
          position: absolute;
          top: 70px;
          right: 0;
          width: 1.5px;
          height: 140px;
          background: var(--accent-shade-2);
        }
      }

      .answer {
        padding-left: var(--spacing-30);
        padding-right: 0;
        height: 100%;
      }
    }

    &.known,
    &.reviewed {
      .status {
        border-color: var(--accent-shade-1);
        background: var(--accent);
      }
    }

    &.unknown,
    &.not-reviewed {
      border-color: var(--main-red);

      .status {
        background: var(--main-red);
        border-color: rgba(197, 2, 2, 0.5);
      }
    }

    &.skipped {
      border-color: var(--main-dark-light-4);

      .status {
        background: var(--main-dark-light-4);
        border-color: #545C69;
      }
    }
  }
}

@include bp($bp-laptop) {
  .deck-terms {
    .h3 {
      font-size: 24px;
      margin-bottom: var(--spacing-20);
    }

    .card {
      height: 360px;

      .card-holder {
        .question:after {
          background: var(--accent-shade-2);
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .deck-terms {
    .h3 {
      font-size: 21px;
      margin-top: var(--spacing-30);
      margin-bottom: var(--spacing-20);
    }

    .card {
      padding: var(--spacing-22) var(--spacing-20);
      height: 500px;

      .card-number {
        font-size: 14px;
        margin-bottom: var(--spacing-12);
      }

      .card-holder {
        .answer,
        .question {
          padding-left: 0;
          padding-right: 0;
          height: 50%;
        }

        .question {
          margin-bottom: var(--spacing-12);

          &:after {
            height: 1px;
            width: calc(100% - 66px);
            left: 33px;
            bottom: -12px;
            top: unset;
          }
        }

        .answer {
          margin-top: var(--spacing-12);
        }
      }

      .status {
        left: unset;
        right: 7px;
        top: 8px;
        font-size: 11px;
        padding: 0 var(--spacing-10);
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
      }
    }

    .heading-dropdown {
      flex-direction: column;
      margin-bottom: var(--spacing-40);

      .h3 {
        margin-bottom: var(--spacing-12);
      }

      .dropdown {
        width: 200px;
      }
    }
  }
}
</style>
